package dev.lingvo.site.components

import androidx.compose.runtime.Composable
import androidx.compose.runtime.LaunchedEffect
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.rememberUpdatedState
import androidx.compose.runtime.setValue
import com.varabyte.kobweb.compose.foundation.layout.Column
import com.varabyte.kobweb.compose.ui.Modifier
import com.varabyte.kobweb.compose.ui.modifiers.fillMaxWidth
import com.varabyte.kobweb.compose.ui.modifiers.gap
import dev.lingvo.shared.ReadinessDto
import dev.lingvo.shared.Role
import dev.lingvo.shared.WorkspaceDto
import dev.lingvo.site.api.ApiClient
import dev.lingvo.site.api.ApiException
import dev.lingvo.site.api.Async
import dev.lingvo.site.api.Reloadable
import dev.lingvo.site.api.Session
import dev.lingvo.site.ds.LCard
import dev.lingvo.site.ds.Space
import dev.lingvo.site.state.ProjectActionKey
import dev.lingvo.site.state.ProjectAuthority
import dev.lingvo.site.state.VerifiedProjectRoute
import dev.lingvo.site.state.actionKey
import dev.lingvo.site.state.liveProjectRoute
import dev.lingvo.site.state.requestProjectResource
import kotlinx.coroutines.CancellationException

/** The exact membership snapshot allowed to start one readiness request. */
private data class ProjectReadinessRequest(
    val key: ProjectActionKey,
    val role: Role,
    val memberships: List<WorkspaceDto>,
)

private fun captureProjectReadinessRequest(
    key: ProjectActionKey,
    expectedRole: Role?,
    currentRoute: () -> VerifiedProjectRoute?,
    currentMemberships: () -> List<WorkspaceDto>,
): ProjectReadinessRequest? {
    val role = expectedRole ?: return null
    val route = currentRoute()?.takeIf { it.actionKey() == key } ?: return null
    val memberships = currentMemberships()
    if (ProjectAuthority.resolve(route.project, memberships).role != role) return null
    return ProjectReadinessRequest(key, role, memberships)
}

private suspend fun requestProjectReadiness(
    request: ProjectReadinessRequest,
    currentRoute: () -> VerifiedProjectRoute?,
    currentMemberships: () -> List<WorkspaceDto>,
    transport: suspend (projectSlug: String) -> ReadinessDto,
): ReadinessDto = requestProjectResource(
    key = request.key,
    expectedSlug = request.key.routeSlug,
    memberships = request.memberships,
    currentRoute = currentRoute,
    currentMemberships = currentMemberships,
) { exactRoute ->
    transport(exactRoute.project.slug)
}

/**
 * Can we ship, bound to the exact verified project route and role rendering this band.
 *
 * Five counters per language and one sentence over them come from the same readiness call as the CI
 * gate. Counts stay actionable; a percentage would hide which language is actually blocking a
 * release. [refreshSignal] lets the overview's refresh reload this independent child as well.
 */
@Composable
internal fun ReadinessBand(
    key: ProjectActionKey,
    authority: ProjectAuthority,
    framed: Boolean = true,
    refreshSignal: Int = 0,
    currentRoute: () -> VerifiedProjectRoute? = { liveProjectRoute(key.routeSlug) },
    currentMemberships: () -> List<WorkspaceDto> = { Session.organizations },
    transport: suspend (projectSlug: String) -> ReadinessDto = ApiClient::readiness,
    /**
     * Hands the settled readiness up so the section heading above can state it.
     *
     * The band owns the request; the heading owns the sentence. Published from a `LaunchedEffect`
     * rather than from composition, because writing a caller's state while it is composing is how a
     * heading and the rows under it end up one frame apart.
     */
    onLoaded: (ReadinessDto) -> Unit = {},
    /**
     * The runs every row in this band shares, or null to let each row own the one it starts.
     *
     * Handed in by a test that has to watch what a run does across a reload; production passes
     * nothing, because a run belongs to the language it was started for.
     */
    runs: ReadinessRunStore? = null,
    onNavigate: (String) -> Unit,
) {
    var retrySignal by remember { mutableStateOf(0) }
    val readiness = rememberProjectReadiness(
        key = key,
        expectedRole = authority.role,
        refreshSignal = refreshSignal,
        retrySignal = retrySignal,
        currentRoute = currentRoute,
        currentMemberships = currentMemberships,
        transport = transport,
    )
    // Disclosure is remembered by exact project and role. A reload keeps the languages somebody
    // opened; navigation or a role transition retires the old presentation state.
    var expanded by remember(key, authority.role) { mutableStateOf(false) }
    val state = readiness?.state
    val settled = (state as? Async.Success)?.value
    LaunchedEffect(settled) { settled?.let(onLoaded) }
    // **Nothing at all, not even a skeleton.** A missing entry claim is a retired authority, and
    // `ProjectReadinessAuthorityBrowserTest` — "retired authority cannot publish loading" — pins
    // that such a claim publishes no `role="status"` and no "Checking release readiness". The
    // heading above stays, so the question the page asks is still on screen; what must not appear
    // is a placeholder claiming a request is in flight for somebody who may not make one.
    if (readiness == null || state == null) return

    val content: @Composable () -> Unit = {
        ReadinessContent(
            slug = key.routeSlug,
            state = state,
            authority = authority,
            framed = framed,
            expanded = expanded,
            onExpandedChange = { expanded = it },
            onRetry = { retrySignal++ },
            onNavigate = onNavigate,
            refreshError = readiness.refreshError?.readable(),
            runs = runs,
            // The row's "Fix the first QA error" suspends before it navigates. The same capture the
            // readiness read itself is bound to decides whether that navigation may still be
            // published: a route, session or role change between the press and the answer retires
            // it, rather than sending the reader into a project they are no longer looking at.
            remainsCurrent = {
                captureProjectReadinessRequest(
                    key = key,
                    expectedRole = authority.role,
                    currentRoute = currentRoute,
                    currentMemberships = currentMemberships,
                ) != null
            },
        )
    }
    if (framed) {
        LCard(Modifier.fillMaxWidth()) {
            Column(Modifier.fillMaxWidth().gap(Space.LG)) { content() }
        }
    } else {
        Column(Modifier.fillMaxWidth().gap(Space.LG)) { content() }
    }
}

/**
 * Exact live-project boundary for readiness reads.
 *
 * The role is part of the subject even though readiness itself is readable by every project role:
 * a late admin response may contain a structure action in its empty-state presentation, so a
 * demotion to viewer must retire the response just as surely as a route or session change.
 */
internal suspend fun loadProjectReadiness(
    key: ProjectActionKey,
    expectedRole: Role?,
    currentRoute: () -> VerifiedProjectRoute?,
    currentMemberships: () -> List<WorkspaceDto>,
    transport: suspend (projectSlug: String) -> ReadinessDto,
): ReadinessDto {
    val request = captureProjectReadinessRequest(
        key = key,
        expectedRole = expectedRole,
        currentRoute = currentRoute,
        currentMemberships = currentMemberships,
    ) ?: throw CancellationException("The live project authority retired before readiness could start.")
    return requestProjectReadiness(request, currentRoute, currentMemberships, transport)
}

/**
 * Readiness state whose publications are keyed to project, session and exact role.
 *
 * A missing entry claim publishes nothing—the band above it draws the skeleton. After transport begins,
 * [requestProjectResource] converts route/session/role retirement into cancellation before this
 * effect can publish success or failure, and cancellation deliberately has no `finally` mutation.
 *
 * **A refresh keeps what it is refreshing, which is not a preference.** This blanked itself to
 * [Async.Loading] on every signal, [refreshSignal] included — and that signal is the overview's own
 * Refresh button. `LAsyncContent` then unmounted every readiness row, each row's `DisposableEffect`
 * disposed the `ReadinessRunStore` it owned, and a pre-translate run somebody had started from that
 * row lost its poll: no progress, no end-of-run receipt, for work that was still spending credits.
 * The shape is [loadReloadable]'s — blank only when there is nothing worth keeping, and a failed
 * refresh stands beside the last good answer rather than replacing it — with the capture guard the
 * shared helper cannot express: a retired authority still publishes nothing at all.
 */
@Composable
internal fun rememberProjectReadiness(
    key: ProjectActionKey,
    expectedRole: Role?,
    refreshSignal: Int,
    retrySignal: Int,
    currentRoute: () -> VerifiedProjectRoute?,
    currentMemberships: () -> List<WorkspaceDto>,
    transport: suspend (projectSlug: String) -> ReadinessDto,
): Reloadable<ReadinessDto>? {
    var state by remember(key, expectedRole) { mutableStateOf<Async<ReadinessDto>?>(null) }
    var refreshing by remember(key, expectedRole) { mutableStateOf(false) }
    var refreshError by remember(key, expectedRole) { mutableStateOf<Async.Failure?>(null) }
    val latestRoute by rememberUpdatedState(currentRoute)
    val latestMemberships by rememberUpdatedState(currentMemberships)
    val latestTransport by rememberUpdatedState(transport)

    LaunchedEffect(key, expectedRole, refreshSignal, retrySignal) {
        val request = captureProjectReadinessRequest(
            key = key,
            expectedRole = expectedRole,
            currentRoute = latestRoute,
            currentMemberships = latestMemberships,
        ) ?: return@LaunchedEffect
        val previousSuccess = state as? Async.Success<ReadinessDto>
        if (previousSuccess != null) refreshing = true else state = Async.Loading
        refreshError = null
        val result: Async<ReadinessDto> = try {
            Async.Success(
                requestProjectReadiness(
                    request = request,
                    currentRoute = latestRoute,
                    currentMemberships = latestMemberships,
                    transport = latestTransport,
                ),
            )
        } catch (cancelled: CancellationException) {
            throw cancelled
        } catch (failure: Throwable) {
            Async.Failure(
                message = failure.message?.takeIf(String::isNotBlank)
                    ?: "Couldn't load this content. Retry?",
                status = (failure as? ApiException)?.status,
                code = (failure as? ApiException)?.code,
            )
        }
        // A refresh that failed over a band still on screen is a notice beside it, not a retry
        // card in place of it. An initial failure has nothing to stand beside and stays in `state`.
        if (result is Async.Failure && previousSuccess != null) {
            refreshError = result
            state = previousSuccess
        } else {
            state = result
        }
        refreshing = false
    }
    return state?.let { Reloadable(it, refreshing, refreshError) }
}
